import React from 'react';
import { Card, CardContent } from '../ui/card';
import { ArrowDown, Info } from 'lucide-react';

const ordSuffix = (n) => {
  const v = n % 100;
  if (v >= 11 && v <= 13) return 'th';
  return ['th', 'st', 'nd', 'rd'][n % 10] || 'th';
};

const CHAIN_CATS = [
  { id: 'lei_scores', label: 'Conditions', sub: 'LEI Measures — your navigation conditions' },
  { id: 'kpi_gaps', label: 'Outcomes', sub: 'KPI Performance — business outcomes delivered' },
  { id: 'roi_metrics', label: 'Value', sub: 'ROI Metrics — financial value captured' },
];

/**
 * ValueTransferTab — synthesis view, not a new calculation. Arranges
 * the org's existing benchmark percentiles along the value transfer
 * chain (Conditions → Outcomes → Value) so users can see where value
 * is being lost relative to peers.
 */
export default function ValueTransferTab({ myPosition, industryName }) {
  const comps = myPosition?.comparisons || [];
  if (!myPosition?.has_data || comps.length === 0) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-6 flex items-center gap-3 text-sm text-slate-600">
          <Info className="h-5 w-5 text-blue-500" />
          Import KPI actuals, LEI measure data, or run calculations to see your value transfer position against peers.
        </CardContent>
      </Card>
    );
  }

  const stages = CHAIN_CATS.map((cat) => {
    const catComps = comps.filter((c) => c.category === cat.id);
    if (catComps.length === 0) return { ...cat, pct: null };
    const pct = catComps.reduce((sum, c) => sum + (c.percentile_rank || 0), 0) / catComps.length;
    return { ...cat, pct: Math.round(pct), count: catComps.length };
  });
  const known = stages.filter((s) => s.pct != null);
  const weakestId = known.length > 1 ? known.reduce((a, b) => (b.pct < a.pct ? b : a)).id : null;

  return (
    <div className="max-w-2xl" data-testid="value-transfer-tab-content">
      <p className="text-sm text-slate-600 mb-4">
        Your benchmark percentiles arranged along the value transfer chain — where peers in {industryName || 'your industry'} are gaining or losing ground against you.
      </p>
      <div className="flex flex-col items-stretch">
        {stages.map((stage, i) => (
          <React.Fragment key={stage.id}>
            <Card
              className={stage.id === weakestId ? 'border-amber-300 ring-1 ring-amber-200 bg-amber-50/40' : 'border-slate-200'}
              data-testid={`vt-stage-${stage.id}`}
            >
              <CardContent className="p-4 flex items-center justify-between gap-4">
                <div>
                  <p className="font-bold text-slate-900">
                    {stage.label}
                    {stage.id === weakestId && (
                      <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5" data-testid="vt-weakest-tag">
                        Where value is being lost vs peers
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">{stage.sub}</p>
                </div>
                <div className="text-right shrink-0">
                  {stage.pct != null ? (
                    <>
                      <p className="text-2xl font-extrabold tabular-nums text-slate-900">{stage.pct}<span className="text-sm font-semibold text-slate-500">{ordSuffix(stage.pct)}</span></p>
                      <div className="w-28 h-1.5 bg-slate-100 rounded-full mt-1.5 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${stage.id === weakestId ? 'bg-amber-500' : 'bg-indigo-500'}`}
                          style={{ width: `${stage.pct}%` }}
                        />
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">percentile · {stage.count} metric{stage.count === 1 ? '' : 's'}</p>
                    </>
                  ) : (
                    <p className="text-sm text-slate-400">No data</p>
                  )}
                </div>
              </CardContent>
            </Card>
            {i < stages.length - 1 && (
              <div className="flex justify-center py-1">
                <ArrowDown className="h-4 w-4 text-slate-300" />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
      <p className="text-xs text-slate-500 italic mt-4">
        No new calculations — this is a synthesis of your existing benchmark percentiles, grouped by where each metric sits in the value transfer chain.
      </p>
    </div>
  );
}
